<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import AppIcon from './AppIcon.vue'

const props = withDefaults(
  defineProps<{
    src?: string | null
    /** Initials shown without an image (or when it fails to load). */
    text?: string
    /** Feather icon shown without image or text. */
    icon?: string
    /** Pixels (legacy sizes 35/38/40/48, the profile's 6 rem = 84 px). */
    size?: number
    variant?: 'light-primary' | 'primary' | 'light-secondary' | 'light-success' | 'light-danger' | 'light-warning'
    /** Status dot on the bottom-right corner (legacy `badge` + `badge-variant`, the user menu's green dot). */
    badge?: 'success' | 'danger' | 'warning' | 'primary' | null
    /** Accessible name / tooltip text. */
    title?: string
    to?: RouteLocationRaw
  }>(),
  { src: null, text: '', icon: 'UserIcon', size: 38, variant: 'light-primary', badge: null, title: '', to: undefined },
)

const failed = ref(false)
watch(
  () => props.src,
  () => (failed.value = false),
)
const showImage = computed(() => Boolean(props.src) && !failed.value)
const style = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  fontSize: `${Math.max(10, Math.round(props.size * 0.36))}px`,
}))
</script>

<template>
  <component
    :is="to ? 'RouterLink' : 'span'"
    :to="to"
    class="app-avatar"
    :class="`app-avatar--${variant}`"
    :style="style"
    :title="title || undefined"
    :aria-label="title || undefined"
    data-kit="avatar"
  >
    <img v-if="showImage" :src="src ?? undefined" :alt="title" class="app-avatar__image" @error="failed = true" />
    <span v-else-if="text" class="app-avatar__text" data-testid="avatar-text">{{ text }}</span>
    <AppIcon v-else :name="icon" :size="Math.round(size * 0.45)" />
    <span v-if="badge" class="app-avatar__badge" :class="`app-avatar__badge--${badge}`" data-testid="avatar-badge" />
  </component>
</template>

<style scoped>
.app-avatar {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
}
.app-avatar__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.app-avatar--light-primary {
  color: var(--p-primary-500);
  background: color-mix(in srgb, var(--p-primary-500) 12%, transparent);
}
.app-avatar--primary {
  color: #fff;
  background: var(--p-primary-500);
}
.app-avatar--light-secondary {
  color: var(--p-surface-600);
  background: color-mix(in srgb, var(--p-surface-600) 12%, transparent);
}
.app-avatar--light-success {
  color: var(--p-green-500);
  background: color-mix(in srgb, var(--p-green-500) 12%, transparent);
}
.app-avatar--light-danger {
  color: var(--p-red-500);
  background: color-mix(in srgb, var(--p-red-500) 12%, transparent);
}
.app-avatar--light-warning {
  color: var(--p-orange-500);
  background: color-mix(in srgb, var(--p-orange-500) 12%, transparent);
}
.app-avatar__badge {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 11px;
  height: 11px;
  border: 2px solid #fff;
  border-radius: 50%;
}
.app-avatar__badge--success {
  background: var(--p-green-500);
}
.app-avatar__badge--danger {
  background: var(--p-red-500);
}
.app-avatar__badge--warning {
  background: var(--p-orange-500);
}
.app-avatar__badge--primary {
  background: var(--p-primary-500);
}
</style>
